/**
 * `useTreeUiStore` — the shared, transient UI state for the Map editor:
 * which view mode is active, which node is selected/focused, which subtrees
 * are collapsed, and (PR-2) which node's title is being edited inline.
 *
 * Issue #759 (Phase 2). §A7 asks for **one** well-named state module that
 * BOTH editors consume so they stay in sync: clicking a node in the outline
 * focuses it on the tree and vice-versa, all through this single store. The
 * store holds *only* UI state; the node data itself lives in the
 * TanStack-Query cache (`useTaskNodes`), and persisted node fields
 * (title/state/parent/order) go through the mutation hooks. Collapse +
 * view-mode + the inline-edit cursor are deliberately client-only (not
 * persisted).
 *
 * Where each piece of state is held (the "obvious abstraction boundaries"
 * the founder asks for):
 *   - `viewMode`             — which editor surface is showing (tree / outline / split).
 *   - `selectedId`           — the focused node; shared selection is what makes
 *                              the two panes mirror each other (B2.4).
 *   - `editingNodeId`        — the node whose title is being typed (the outline
 *                              cursor); null when nothing is being edited.
 *   - `collapsed`            — folded subtrees on the canvas (B8.3).
 *   - `openNodeId`           — the node whose detail drawer is open (B2.5), or
 *                              null when the drawer is closed. The drawer floats
 *                              over the (dimmed) live editor, so this is a
 *                              separate axis from selection: a node can be
 *                              focused on the canvas without its drawer open.
 *   - `openTab` / `focusNoteId` — the DEEP-LINK hint (#907). When a notification
 *                              click opens a node, it can also request the drawer
 *                              open on a specific tab ("notes") and scroll to /
 *                              briefly highlight a specific note id. These are a
 *                              one-shot hint the drawer reads when it (re)opens
 *                              on the target node; the drawer clears them once
 *                              consumed (so a later manual tab change isn't
 *                              fought, and the highlight is transient). Kept here
 *                              — one well-named axis both editors read from —
 *                              rather than threaded through props, mirroring how
 *                              `openNodeId` itself lives here.
 *
 * PR-1 established `selectedId` + `collapsed` (the select-only rail + the
 * canvas). PR-2 added `viewMode` + `editingNodeId`. #760 added `openNodeId`
 * (the detail-drawer's open node) here — the same seam. #907 extends `openNode`
 * with an optional `{tab, noteId}` focus hint for notification deep-links,
 * without disturbing the selection/collapse/edit contract.
 *
 * Zustand (already a dependency) keeps this dependency-free and testable.
 * It is intentionally NOT keyed by team: each MapEditor mount resets it via
 * `reset()`, so a single global store is sufficient and simplest.
 */

import { create } from "zustand";

/** The three editor surfaces the gallery ships (B2.1 / B2.3 / B2.4). */
export type ViewMode = "tree" | "outline" | "split";

/** Which drawer tab a deep-link asks the drawer to open on (#907). */
export type DrawerOpenTab = "notes";

/**
 * An optional focus hint passed to `openNode` (#907). A notification deep-link
 * uses it to land the drawer on the Notes tab AND scroll to / highlight the
 * referenced note. Omitted for a plain drawer open (the drawer keeps its
 * default tab + no highlight).
 */
export interface OpenNodeFocus {
  /** Open the drawer on this tab (currently only "notes"). */
  tab?: DrawerOpenTab;
  /** Scroll to / briefly highlight this note id in the Notes tab. */
  noteId?: string;
}

interface TreeUiState {
  /** Which editor surface is showing. */
  viewMode: ViewMode;
  /** Currently focused node id (B2.2 / shared selection), or null. */
  selectedId: string | null;
  /**
   * Currently selected dependency EDGE id (#929), or null. A separate axis
   * from `selectedId` (the node selection): the two are mutually exclusive —
   * selecting an edge clears the node selection and vice-versa — so the editor
   * never shows a node popover and an edge popover at once. Only meaningful
   * when the dependency layer is on; cleared on a background click / Esc /
   * team switch, exactly like the node selection.
   */
  selectedEdgeId: string | null;
  /** Node whose title is being edited inline (the outline cursor), or null. */
  editingNodeId: string | null;
  /** Node ids whose subtrees are collapsed (B8.3). */
  collapsed: Set<string>;
  /** Node whose detail drawer is open (B2.5), or null when the drawer is closed. */
  openNodeId: string | null;
  /**
   * Deep-link hint (#907): which tab the drawer should open on for the current
   * `openNodeId`, or null for the default. A one-shot the drawer consumes on
   * (re)open of the target node.
   */
  openTab: DrawerOpenTab | null;
  /**
   * Deep-link hint (#907): a note id to scroll to / briefly highlight in the
   * Notes tab, or null. A one-shot the Notes tab consumes (then clears via
   * `clearFocusNote`) so the highlight is transient.
   */
  focusNoteId: string | null;
  /**
   * Click-to-link arming (#904, B2.7). When a node id is set, the editor is in
   * "pick the task this one depends on / comes after" mode: the armed node is
   * the SOURCE (it comes BEFORE the next node clicked), a banner cue shows, and
   * the next node click draws the dependency. Null = not arming. Kept here (not
   * local canvas state) so the click-to-link mode is one well-named axis the
   * canvas + the editor banner read from one place, mirroring `selectedId`.
   */
  linkArmId: string | null;

  /** Switch the active editor surface (tree / outline / split). */
  setViewMode: (mode: ViewMode) => void;
  /** Focus a node (idempotent). Pass null to clear focus. Also clears any edge selection. */
  select: (id: string | null) => void;
  /**
   * Select a dependency edge (#929). Pass null to clear. Selecting an edge
   * clears the node selection (and any armed click-to-link) so the two
   * selections stay mutually exclusive.
   */
  selectEdge: (id: string | null) => void;
  /** Begin inline-editing a node's title (also focuses it). Null = stop editing. */
  setEditing: (id: string | null) => void;
  /**
   * Open the detail drawer for a node (also focuses it). Null = close the
   * drawer. The optional `focus` (#907) carries a deep-link hint: which tab to
   * open on + a note id to highlight. A plain `openNode(id)` clears any prior
   * hint, so a manual open never inherits a stale deep-link.
   */
  openNode: (id: string | null, focus?: OpenNodeFocus) => void;
  /** Clear the transient note-highlight hint once the Notes tab has consumed it (#907). */
  clearFocusNote: () => void;
  /** Arm click-to-link from a node (it becomes the link SOURCE). Null = cancel. */
  setLinkArm: (id: string | null) => void;
  /** Toggle a node's collapsed state. */
  toggleCollapse: (id: string) => void;
  /** Expand a node if it is collapsed (no-op otherwise). */
  expand: (id: string) => void;
  /** Reset all UI state (called on team switch / unmount). */
  reset: () => void;
}

export const useTreeUiStore = create<TreeUiState>((set) => ({
  viewMode: "tree",
  selectedId: null,
  selectedEdgeId: null,
  editingNodeId: null,
  collapsed: new Set<string>(),
  openNodeId: null,
  openTab: null,
  focusNoteId: null,
  linkArmId: null,

  setViewMode: (mode) => set({ viewMode: mode }),

  // Selecting a node always disarms a pending click-to-link (a fresh focus is
  // not the link target — the canvas's link-target click handler runs first and
  // clears the arm itself; a plain background/other select is a cancel). It also
  // clears any selected edge (the two selections are mutually exclusive, #929).
  select: (id) => set({ selectedId: id, selectedEdgeId: null, linkArmId: null }),

  // Selecting an edge clears the node selection + any armed click-to-link, so
  // the editor shows exactly one popover at a time (#929).
  selectEdge: (id) =>
    set(
      id === null
        ? { selectedEdgeId: null }
        : { selectedEdgeId: id, selectedId: null, linkArmId: null },
    ),

  setLinkArm: (id) => set({ linkArmId: id }),

  // Editing a node implies it is also the selected/focused node, so the two
  // panes agree on what the cursor is on.
  setEditing: (id) =>
    set(id === null ? { editingNodeId: null } : { editingNodeId: id, selectedId: id }),

  // Opening the drawer also focuses the node (the gallery dims the rest of the
  // tree and highlights the open node — B2.5), so selection follows the drawer.
  // A deep-link (#907) may also carry a one-shot focus hint (tab + note id) the
  // drawer reads on (re)open; a plain open clears any prior hint so a manual
  // open never inherits a stale deep-link.
  openNode: (id, focus) =>
    set(
      id === null
        ? { openNodeId: null, openTab: null, focusNoteId: null }
        : {
            openNodeId: id,
            selectedId: id,
            openTab: focus?.tab ?? null,
            focusNoteId: focus?.noteId ?? null,
          },
    ),

  clearFocusNote: () => set({ focusNoteId: null }),

  toggleCollapse: (id) =>
    set((state) => {
      const next = new Set(state.collapsed);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { collapsed: next };
    }),

  expand: (id) =>
    set((state) => {
      if (!state.collapsed.has(id)) return {};
      const next = new Set(state.collapsed);
      next.delete(id);
      return { collapsed: next };
    }),

  reset: () =>
    set({
      viewMode: "tree",
      selectedId: null,
      selectedEdgeId: null,
      editingNodeId: null,
      collapsed: new Set<string>(),
      openNodeId: null,
      openTab: null,
      focusNoteId: null,
      linkArmId: null,
    }),
}));
